/**
 * Fallback symbol styling.
 *
 * Hand-made palettes used to live here, one table per game. They now live
 * in `themes/<id>/theme.json`, because a colour table is design data and
 * shipping design data as source code means every new theme is a code
 * change (see `docs/theme-pipeline.md`).
 *
 * What stays here is the part that CANNOT be data: the rule that produces
 * a readable tile for a symbol nobody has themed yet. A game appears in
 * the lobby the moment its math is released, and it has to be playable and
 * legible before an artist ever opens it.
 */

export interface SymbolStyle {
  /** Tile fill. Used when there is no sprite frame. */
  fill: number
  /** Label colour. */
  text: number
  /** Text on the tile. Whole word — "FS" style abbreviations do not read. */
  label: string
  /**
   * Label size. A property of the symbol rather than a branch in the
   * renderer: "DRAGON" and "10" cannot share a size, and a new symbol must
   * not require touching draw code.
   */
  fontSize: number
  /** Full name — for the paytable and tooltips. */
  name: string
  /** Atlas frame, when the theme ships art for this symbol. */
  frame?: string
  /** Standalone image URL — the no-atlas path. `frame` takes precedence. */
  image?: string
  /**
   * Per-symbol backdrop URL, drawn under `image` and stretched to the cell.
   * Absent ⇒ the flat `fill` tile.
   */
  backgroundImage?: string
  /**
   * Size relative to the other symbols. Absent ⇒ 1, the fitted box as-is.
   */
  scale?: number
  /**
   * Flipbook frames (URLs) and rate. Textures are static rasters, so a
   * "living" symbol is several full images swapped by the renderer.
   */
  animation?: { images: string[]; fps: number }
}

const PLACEHOLDER: SymbolStyle = {
  fill: 0x2a2f3a,
  text: 0x8a93a5,
  label: '?',
  fontSize: 40,
  name: 'Unknown',
}

/** The minimum the client knows about a symbol (from GET /games/:id). */
export interface GameSymbol {
  id: number
  code: string
  name: string
  kind: string
}

/** Stable string hash — a symbol's colour must not jump between renders. */
function hash(text: string): number {
  let h = 2166136261
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function hsl(h: number, s: number, l: number): number {
  const a = s * Math.min(l, 1 - l)
  const f = (n: number): number => {
    const k = (n + h / 30) % 12
    return Math.round(255 * (l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))))
  }
  return (f(0) << 16) | (f(8) << 8) | f(4)
}

/** Tile caption: the symbol name, trimmed to something readable. */
function labelFor(sym: GameSymbol): { label: string; fontSize: number } {
  const label = sym.name.trim().toUpperCase().slice(0, 9)
  const fontSize = label.length <= 2 ? 38 : label.length <= 4 ? 26 : label.length <= 6 ? 20 : 15
  return { label, fontSize }
}

/**
 * Generated style for an unthemed symbol.
 *
 * Hue comes from the symbol code (stable), saturation and lightness from
 * its role: scatter and wild must stand out, high symbols must read as
 * richer than low ones. This is not design — it is a legible placeholder,
 * and every field of it can be overridden by a theme.
 */
export function generatedStyle(sym: GameSymbol): SymbolStyle {
  const hue = hash(sym.code) % 360
  const { label, fontSize } = labelFor(sym)

  if (sym.kind === 'scatter') {
    return { fill: 0xe0457b, text: 0xfff0f5, label, fontSize, name: sym.name }
  }
  if (sym.kind === 'wild') {
    return { fill: 0xf5c518, text: 0x1a1d24, label, fontSize, name: sym.name }
  }
  const high = sym.kind === 'high'
  return {
    fill: hsl(hue, high ? 0.62 : 0.22, high ? 0.45 : 0.28),
    text: hsl(hue, high ? 0.9 : 0.35, high ? 0.94 : 0.82),
    label,
    fontSize,
    name: sym.name,
  }
}

/** Generated palette for a whole game — the no-theme baseline. */
export function generatedPalette(symbols: readonly GameSymbol[]): Record<string, SymbolStyle> {
  const palette: Record<string, SymbolStyle> = {}
  for (const sym of symbols) palette[sym.code] = generatedStyle(sym)
  return palette
}

export function styleFrom(
  palette: Record<string, SymbolStyle>,
  code: string,
): SymbolStyle {
  return palette[code] ?? { ...PLACEHOLDER, label: code }
}
